import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Lock, Unlock, ChevronLeft, ChevronRight, 
  BookMarked, List, X, Eye, ShoppingCart, Star, Award,
  Shield, Sparkles, Clock, FileText, CheckCircle
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useHero } from '@/hooks/useHero';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Copy protection styles
const copyProtectionStyles = `
  .protected-content {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }
  .protected-content::selection {
    background: transparent;
  }
  .protected-content::-moz-selection {
    background: transparent;
  }
`;

// Floating book particles
const FloatingParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 bg-amber-500/20 rounded-full"
        initial={{ 
          x: Math.random() * 100 + '%', 
          y: '100%',
          opacity: 0 
        }}
        animate={{ 
          y: '-10%',
          opacity: [0, 0.5, 0],
          scale: [0.5, 1, 0.5]
        }}
        transition={{ 
          duration: 8 + Math.random() * 4,
          repeat: Infinity,
          delay: Math.random() * 5,
          ease: "linear"
        }}
      />
    ))}
  </div>
);

// Chapter card component
const ChapterCard = ({ chapter, index, hasAccess, onReadChapter, onGoToPage, onPromptPurchase }) => {
  const isPreview = chapter.is_preview || chapter.number === 1;
  const canAccess = hasAccess || isPreview;
  
  const handleSectionClick = (page) => {
    if (canAccess) {
      onGoToPage(page);
    } else {
      onPromptPurchase();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`relative group rounded-2xl overflow-hidden ${
        canAccess
          ? 'bg-gradient-to-br from-amber-900/30 to-stone-900/50 border border-amber-500/20 hover:border-amber-500/50'
          : 'bg-stone-900/30 border border-stone-700/30'
      } transition-all duration-300`}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80"
            onClick={() => handleSectionClick(chapter.start_page)}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              canAccess
                ? 'bg-amber-500/20 text-amber-400'
                : 'bg-stone-800 text-stone-500'
            }`}>
              <span className="text-xl font-bold">{chapter.number}</span>
            </div>
            <div>
              <h3 className={`text-lg font-bold text-white hover:text-amber-400 transition-colors`}>
                {chapter.title}
              </h3>
              <p className="text-sm text-amber-400/70">{chapter.subtitle}</p>
            </div>
          </div>
          {isPreview && (
            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">
              FREE PREVIEW
            </span>
          )}
        </div>
        
        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{chapter.description}</p>
        
        <div className="flex items-center justify-between">
          <span 
            className="text-xs text-amber-500 cursor-pointer hover:underline"
            onClick={() => handleSectionClick(chapter.start_page)}
          >
            Pages {chapter.start_page} - {chapter.end_page}
          </span>
          
          <button
            onClick={() => canAccess ? onReadChapter(chapter) : onPromptPurchase()}
            data-testid={`read-chapter-${chapter.number}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              canAccess
                ? 'bg-amber-500 text-black hover:bg-amber-400'
                : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
            }`}
          >
            {canAccess ? (
              <>
                <BookOpen className="w-4 h-4" />
                Read Chapter
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                Unlock
              </>
            )}
          </button>
        </div>
        
        {/* Section list - clickable */}
        {chapter.sections && chapter.sections.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <p className="text-xs text-gray-500 mb-2">Sections (click to jump):</p>
            <div className="flex flex-wrap gap-2">
              {chapter.sections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSectionClick(section.page)}
                  className={`px-2 py-1 text-xs rounded transition-all cursor-pointer ${
                    canAccess 
                      ? 'bg-white/5 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300' 
                      : 'bg-white/5 text-gray-400 hover:bg-amber-500/10 hover:text-amber-400'
                  }`}
                >
                  {section.title} <span className="text-gray-500 ml-1">p.{section.page}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Book Reader Modal with copy protection
const BookReader = ({ pageData, onClose, onNavigate, userEmail, totalPages, chapters }) => {
  const contentRef = useRef(null);
  const [showTOC, setShowTOC] = useState(false);
  
  // Extract page info from the API response
  const page = pageData?.page || {};
  const nextPage = pageData?.next_page;
  const prevPage = pageData?.prev_page;
  
  // Full Table of Contents data with all sections
  const fullTOC = [
    { title: "Table of Contents", page: 5, isHeader: true },
    { title: "Chapter 1: The Jinga", page: 9, isChapter: true },
    { title: "1.1 The Jinga", page: 11 },
    { title: "1.1.1 The Jinga inside and outside position", page: 13 },
    { title: "Chapter 2: Basic Capoeira Kicks", page: 17, isChapter: true },
    { title: "2.1 Meia-lua de Frente", page: 18 },
    { title: "2.1.1 Mea Lua de Frente", page: 24 },
    { title: "2.2 Meia-lua de Costas", page: 24 },
    { title: "2.2.1 Foot Positions", page: 24 },
    { title: "2.2.3 Meia-lua de Costas", page: 35 },
    { title: "2.3 Chapa de Frente", page: 41 },
    { title: "2.3.1 Chapa de Frente 2", page: 43 },
    { title: "2.3.2 Chapa de Frente 3", page: 43 },
    { title: "2.3.3 Chapa de Frente 4", page: 44 },
    { title: "2.4 Chapa de Coasta", page: 47 },
    { title: "2.4.1 Chapa de Coasta 1", page: 48 },
    { title: "2.4.2 Chapa de Coasta 2", page: 49 },
    { title: "2.4.3 Chapa de Coasta 3", page: 50 },
    { title: "2.4.4 Chapa de Coasta 4", page: 51 },
    { title: "2.5 Capoeira Angola Kicks", page: 57 },
    { title: "2.5.1 Chapa de Frente Embaixo", page: 57 },
    { title: "2.5.2 Chapa de Frente Embaixo 1", page: 58 },
    { title: "2.5.3 Rabo de Arraia", page: 62 },
    { title: "2.5.4 Armada", page: 64 },
    { title: "2.5.5 Rabo de Arraia", page: 72 },
    { title: "2.5.6 Martelo", page: 76 },
    { title: "Chapter 3: Defensive Movements", page: 81, isChapter: true },
    { title: "3.1 Negativa", page: 84 },
    { title: "3.2 Negativa de Frente", page: 89 },
    { title: "3.3 Negativa Perna na Frente", page: 93 },
    { title: "3.4 Esquiva Lateral", page: 82 },
    { title: "3.5 Esquiva de Frente", page: 88 },
    { title: "3.6 Cocorinha", page: 100 },
    { title: "3.7 Ponte (Bridge)", page: 104 },
    { title: "Chapter 4: Advanced Attack Movements", page: 109, isChapter: true },
    { title: "4.1 Voo de Morcego", page: 109 },
    { title: "4.2 Chapéu de Couro", page: 115 },
    { title: "4.3 Rasteira Em Cima", page: 119 },
    { title: "4.4 Plantar Bananeira e Chapa", page: 125 },
    { title: "Chapter 5: Escape Movements", page: 129, isChapter: true },
    { title: "5.1 Negativa and Sweep", page: 131 },
    { title: "5.2 Negativa e Rasteira Embaixo", page: 132 },
    { title: "5.3 Rolê", page: 135 },
    { title: "5.4 Rolê de Costa", page: 141 },
    { title: "5.5 Rolê de Banco", page: 145 },
    { title: "5.6 Parafuso no Chão", page: 149 },
    { title: "5.7 Aú (Cartwheel)", page: 152 },
    { title: "5.8 Aú de Cabeça", page: 158 },
    { title: "5.9 Aú Fechado", page: 164 },
    { title: "5.10 Plantar Bananeira", page: 168 },
    { title: "5.11 Cabecada (Headbutt)", page: 176 },
    { title: "Chapter 6: Chamada", page: 201, isChapter: true },
    { title: "6.1 Introduction to Chamada", page: 201 },
    { title: "6.2 Scissors & Alligator", page: 210 },
    { title: "6.3 Chamada 1 and 2", page: 225 },
    { title: "6.4 Advanced Chamada", page: 240 },
    { title: "Chapter 7: Musical Instruments", page: 251, isChapter: true },
    { title: "7.1 The Berimbau", page: 252 },
    { title: "7.2 The Atabaque", page: 265 },
    { title: "7.3 The Pandeiro", page: 275 },
    { title: "7.4 Playing the Pandeiro", page: 285 },
    { title: "7.5 Capoeira Songs", page: 295 },
    { title: "Chapter 8: Glossary", page: 301, isChapter: true },
    { title: "8.1 A-F Terms", page: 301 },
    { title: "8.2 G-M Terms", page: 308 },
    { title: "8.3 N-S Terms", page: 315 },
    { title: "8.4 T-Z Terms", page: 320 },
  ];
  
  // Disable right-click
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      // Disable Ctrl+C, Ctrl+A, Ctrl+P
      if (e.ctrlKey && ['c', 'a', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        toast.error('Copying is disabled for copyright protection');
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  
  if (!page || !page.page_number) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
    >
      <style>{copyProtectionStyles}</style>
      
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      
      {/* Page counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-black/50 backdrop-blur rounded-full">
        <span className="text-white font-medium">
          Page {page.page_number} of {totalPages}
        </span>
      </div>
      
      {/* Interactive Table of Contents Sidebar */}
      <AnimatePresence>
        {showTOC && (
          <motion.div
            initial={{ x: -400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -400, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            className="absolute top-0 left-0 bottom-0 w-80 bg-stone-900 z-50 shadow-2xl overflow-hidden flex flex-col"
          >
            {/* TOC Header */}
            <div className="p-4 bg-amber-500 flex items-center justify-between">
              <h3 className="font-bold text-black text-lg flex items-center gap-2">
                <BookMarked className="w-5 h-5" />
                Table of Contents
              </h3>
              <button
                onClick={() => setShowTOC(false)}
                className="p-1 hover:bg-black/10 rounded"
              >
                <X className="w-5 h-5 text-black" />
              </button>
            </div>
            
            {/* TOC List */}
            <div className="flex-1 overflow-y-auto p-2">
              {fullTOC.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    onNavigate(item.page);
                    setShowTOC(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded transition-all hover:bg-amber-500/20 ${
                    item.isChapter 
                      ? 'font-bold text-amber-400 mt-3 border-t border-white/10 pt-3' 
                      : item.isHeader
                      ? 'font-bold text-white text-lg mb-2'
                      : 'text-gray-300 text-sm pl-6'
                  } ${page.page_number === item.page ? 'bg-amber-500/30 border-l-4 border-amber-500' : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="truncate pr-2">{item.title}</span>
                    <span className="text-amber-500 text-xs font-mono">p.{item.page}</span>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Current page indicator */}
            <div className="p-3 bg-black/30 text-center text-sm text-gray-400">
              Currently on page {page.page_number}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Navigation buttons */}
      {prevPage && (
        <button
          onClick={() => onNavigate(prevPage)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-amber-500/20 hover:bg-amber-500/40 rounded-full transition-colors"
        >
          <ChevronLeft className="w-8 h-8 text-amber-400" />
        </button>
      )}
      
      {nextPage && (
        <button
          onClick={() => onNavigate(nextPage)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-amber-500/20 hover:bg-amber-500/40 rounded-full transition-colors"
        >
          <ChevronRight className="w-8 h-8 text-amber-400" />
        </button>
      )}
      
      {/* Book page content */}
      <div className="w-full max-w-4xl h-[85vh] mx-4 bg-stone-100 rounded-lg shadow-2xl overflow-hidden relative">
        {/* TOC Toggle Button - Attached to book */}
        <button
          onClick={() => setShowTOC(!showTOC)}
          className="absolute top-4 left-4 z-40 px-4 py-3 bg-amber-500 hover:bg-amber-400 rounded-lg transition-all shadow-lg flex items-center gap-2 group"
          data-testid="toc-toggle-btn"
        >
          <List className="w-5 h-5 text-black" />
          <span className="text-black font-bold">Table of Contents</span>
          <ChevronRight className="w-4 h-4 text-black group-hover:translate-x-1 transition-transform" />
        </button>
        
        {/* Page content - protected */}
        <div 
          ref={contentRef}
          className="protected-content h-full overflow-y-auto flex items-center justify-center bg-stone-200"
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
        >
          {page.content_type === 'placeholder' ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-12">
              <BookOpen className="w-20 h-20 text-stone-300 mb-6" />
              <h2 className="text-2xl font-bold text-stone-600 mb-2">Page {page.page_number}</h2>
              <p className="text-stone-500">Content loading...</p>
              <p className="text-sm text-stone-400 mt-4">
                The full book content is being prepared for digital viewing.
              </p>
            </div>
          ) : page.content_type === 'image' || page.image_url ? (
            /* Image-based page display - fully protected */
            <div className="relative w-full h-full flex items-center justify-center p-4">
              <img loading="lazy" decoding="async" 
                src={`${API}${page.image_url}`}
                alt={`Page ${page.page_number}`}
                className="max-w-full max-h-full object-contain shadow-lg"
                draggable="false"
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                style={{ 
                  pointerEvents: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none'
                }}
              />
              {/* Watermark overlay on image */}
              {userEmail && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div 
                    className="transform rotate-[-30deg] text-2xl font-bold whitespace-nowrap"
                    style={{ 
                      color: 'rgba(0,0,0,0.08)',
                      textShadow: '0 0 1px rgba(255,255,255,0.3)'
                    }}
                  >
                    {userEmail}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Fallback text display */
            <div className="p-12 text-stone-900" style={{ fontFamily: 'Georgia, serif' }}>
              <div className="prose prose-stone max-w-none text-lg leading-relaxed whitespace-pre-wrap">
                {page.text_content?.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Page curl effect */}
        <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-stone-300 to-transparent pointer-events-none" 
          style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
        />
      </div>
    </motion.div>
  );
};

// Main E-Book Page
const KapoeiraBookInner = () => {
  const hero = useHero('kapoeira-book');
  const [bookData, setBookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [showReader, setShowReader] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isAdmin } = useAuth();
  
  // Check access from token
  const checkAccessFromToken = () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'admin') {
          return { hasAccess: true, email: payload.email };
        }
        return { hasAccess: false, email: payload.email };
      } catch (e) {}
    }
    return { hasAccess: false, email: null };
  };
  
  useEffect(() => {
    fetchBookData();
    const tokenCheck = checkAccessFromToken();
    setDebugInfo(tokenCheck);
    if (tokenCheck.hasAccess) {
      setHasAccess(true);
    }
  }, []);
  
  // Handle Stripe success redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPurchase(sessionId);
    }
  }, [searchParams]);
  
  const verifyPurchase = async (sessionId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await fetch(`${API}/api/ebook/verify-purchase?session_id=${sessionId}`, { headers });
      const data = await res.json();
      
      if (data.success) {
        setPurchaseSuccess(true);
        setHasAccess(true);
        toast.success('Purchase successful! You now have full access to the e-book.');
        // Clean up URL
        window.history.replaceState({}, '', '/kapoeira-book');
      } else {
        toast.error(data.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Failed to verify purchase');
    }
  };
  
  useEffect(() => {
    if (isAuthenticated && isAdmin && isAdmin()) {
      setHasAccess(true);
      setDebugInfo({ hasAccess: true, email: user?.email });
    }
  }, [isAuthenticated, user]);
  
  const fetchBookData = async () => {
    try {
      const res = await fetch(`${API}/api/ebook/info`);
      const data = await res.json();
      setBookData(data);
    } catch (err) {
      console.error('Failed to fetch book:', err);
      toast.error('Failed to load book');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBuyNow = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('auth_token');
    let userEmail = debugInfo?.email;
    
    if (!token || !userEmail) {
      toast.error('Please log in to purchase the e-book');
      navigate('/login?redirect=/kapoeira-book');
      return;
    }
    
    setCheckoutLoading(true);
    
    try {
      const res = await fetch(`${API}/api/ebook/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: userEmail })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Checkout failed');
      }
      
      if (data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.message || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  const handleReadPage = async (pageNumber) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await fetch(`${API}/api/ebook/page/${pageNumber}`, { headers });
      
      if (!res.ok) {
        if (res.status === 403) {
          toast.error('Purchase the book to access this page');
          return;
        }
        throw new Error('Failed to load page');
      }
      
      const data = await res.json();
      setCurrentPage(data);
      setShowReader(true);
    } catch (err) {
      console.error('Failed to load page:', err);
      toast.error('Failed to load page');
    }
  };
  
  const handleReadChapter = (chapter) => {
    handleReadPage(chapter.start_page);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }
  
  const settings = bookData?.settings || {};
  const chapters = bookData?.chapters || [];

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Purchase Success Banner */}
      <AnimatePresence>
        {purchaseSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-0 left-0 right-0 z-[110] bg-gradient-to-r from-green-600 to-emerald-600 py-4 px-6"
          >
            <div className="max-w-4xl mx-auto flex items-center justify-center gap-4">
              <CheckCircle className="w-6 h-6 text-white" />
              <span className="text-white font-bold">
                Purchase Complete! You now have lifetime access to the e-book.
              </span>
              <button
                onClick={() => setPurchaseSuccess(false)}
                className="ml-4 p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Admin Status Bar */}
      {debugInfo?.email && (
        <div className={`fixed top-0 left-0 right-0 z-[100] py-2 px-4 text-sm font-medium flex items-center justify-center gap-4 ${
          hasAccess ? 'bg-green-600' : 'bg-yellow-600'
        }`}>
          <span className="flex items-center gap-2">
            {hasAccess ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            {hasAccess 
              ? `✓ Full Book Access: ${debugInfo.email}` 
              : `Logged in as: ${debugInfo.email}`
            }
          </span>
        </div>
      )}
      
      {/* Force Refresh Button - for cache issues */}
      <button
        onClick={() => {
          window.location.href = window.location.pathname + '?v=' + Date.now();
        }}
        className="fixed bottom-4 right-4 z-[200] bg-amber-500 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg hover:bg-amber-400 transition-colors flex items-center gap-2"
        data-testid="force-refresh-btn"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Refresh Page
      </button>
      
      {/* Hero Section */}
      <section className={`relative min-h-screen flex items-center overflow-hidden ${debugInfo?.email ? 'pt-10' : ''}`}>
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-stone-950 via-amber-950/20 to-stone-950" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(217,119,6,0.1),transparent_50%)]" />
        </div>
        
        <FloatingParticles />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          {/* Book Cover */}
          <motion.div
            initial={{ opacity: 0, x: -50, rotateY: -15 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative mx-auto w-80 lg:w-96">
              {/* Book shadow */}
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[90%] h-8 bg-black/50 blur-xl rounded-full" />
              
              {/* Book cover */}
              <motion.div
                whileHover={{ rotateY: 10, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="relative"
                style={{ perspective: 1000 }}
              >
                <img loading="lazy" decoding="async"
                  src={settings.cover_image || "https://www.mestrecurtispierre.com/courses/wp-content/uploads/2020/05/Capoeira-book-front-cover-2-copy-795x1024.jpg"}
                  alt={settings.title}
                  className="w-full rounded-lg shadow-2xl shadow-amber-900/30"
                />
                
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent rounded-lg pointer-events-none" />
              </motion.div>
              
              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="absolute -top-4 -right-4 bg-amber-500 text-black px-4 py-2 rounded-full font-bold text-sm shadow-lg"
              >
                324 Pages
              </motion.div>
            </div>
          </motion.div>
          
          {/* Book Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left"
          >
            <motion.span
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm font-bold mb-6"
            >
              <BookMarked className="w-4 h-4" />
              <PageEditable path="hero.eyebrow" fallback={hero.kicker || "COMPREHENSIVE STUDY GUIDE"} />
            </motion.span>
            
            <h1 className="text-4xl lg:text-6xl font-black text-white mb-4">
              {settings.title || "Kapoeira Angola"}
              <span className="block text-amber-400"><PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Movements"} /></span>
            </h1>
            
            <p className="text-xl text-amber-200/80 mb-4">{settings.subtitle}</p>
            
            <p className="text-gray-400 mb-8 max-w-xl">
              {settings.description}
            </p>
            
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mb-8">
              <div className="flex items-center gap-2 text-gray-400">
                <FileText className="w-5 h-5 text-amber-500" />
                <span><strong className="text-white">{settings.total_pages || 324}</strong> Pages</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <BookOpen className="w-5 h-5 text-amber-500" />
                <span><strong className="text-white">{chapters.length}</strong> Chapters</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Award className="w-5 h-5 text-amber-500" />
                <span>By <strong className="text-white">{settings.author}</strong></span>
              </div>
            </div>
            
            {/* CTA */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
              {hasAccess ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-bold">
                    <Unlock className="w-4 h-4" />
                    Full Access Unlocked
                  </div>
                  <button
                    onClick={() => handleReadPage(1)}
                    data-testid="start-reading-btn"
                    className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center gap-2"
                  >
                    <BookOpen className="w-5 h-5" />
                    Start Reading
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleBuyNow}
                    disabled={checkoutLoading}
                    data-testid="buy-now-btn"
                    className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {checkoutLoading ? (
                      <>
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5" />
                        Buy Now - ${settings.price || 19.99}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleReadPage(1)}
                    data-testid="free-preview-btn"
                    className="px-6 py-4 border border-white/20 text-white font-bold rounded-full hover:bg-white/5 transition-all flex items-center gap-2"
                  >
                    <Eye className="w-5 h-5" />
                    Free Preview
                  </button>
                </>
              )}
            </div>
            
            {/* Security badge */}
            <div className="mt-8 flex items-center gap-3 text-gray-500 text-sm justify-center lg:justify-start">
              <Shield className="w-5 h-5 text-green-500" />
              <span>Protected content • Watermarked • Copy-protected</span>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronRight className="w-8 h-8 text-amber-500 rotate-90" />
        </motion.div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-stone-950 to-stone-900">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-400 font-bold text-sm tracking-wider">WHAT YOU'LL LEARN</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mt-2">
              Master the <span className="text-amber-400">Ancient Art</span>
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Star, title: "Authentic Techniques", desc: "Learn directly from Mestre Curtis Pierre's 38+ years of experience" },
              { icon: BookOpen, title: "Illustrated Guide", desc: "324 pages of detailed illustrations showing every movement" },
              { icon: Sparkles, title: "Cultural Heritage", desc: "Understand the Kongo origins and spiritual aspects of Capoeira" }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-stone-900/50 border border-stone-800 rounded-2xl p-8 text-center hover:border-amber-500/30 transition-colors"
              >
                <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-8 h-8 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Chapters Section */}
      <section className="py-20 px-6 bg-stone-900">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-400 font-bold text-sm tracking-wider">TABLE OF CONTENTS</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mt-2">
              Book <span className="text-amber-400">Chapters</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              {chapters.length} comprehensive chapters covering everything from basic movements to advanced techniques
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {chapters.map((chapter, idx) => (
              <ChapterCard
                key={chapter.id || idx}
                chapter={chapter}
                index={idx}
                hasAccess={hasAccess}
                onReadChapter={handleReadChapter}
                onGoToPage={handleReadPage}
                onPromptPurchase={() => {
                  toast.info('Purchase the book to access all chapters!', {
                    action: {
                      label: 'Buy Now',
                      onClick: () => handleBuyNow()
                    }
                  });
                }}
              />
            ))}
          </div>
          
          {chapters.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-stone-700 mx-auto mb-4" />
              <p className="text-gray-500">Book chapters are being prepared...</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Book Reader Modal */}
      <AnimatePresence>
        {showReader && currentPage && (
          <BookReader
            pageData={currentPage}
            onClose={() => setShowReader(false)}
            onNavigate={handleReadPage}
            userEmail={debugInfo?.email}
            totalPages={settings.total_pages || 324}
            chapters={chapters}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

export const KapoeiraBook = () => (
  <>
    <KapoeiraBookInner />
    <AdminEditorLink to="/admin/dashboard#kapoeira-editor-btn" label="Edit Kapoeira Book" />
  </>
);

export default KapoeiraBook;
